import React, { useEffect, useState } from 'react';
import { glCodes } from '../services/api';

const TYPE_COLORS = {
  'Income':                  { bg: 'bg-green-50',  border: 'border-green-200', badge: 'bg-green-100 text-green-800',  dot: 'bg-green-500' },
  'Operating Expense':       { bg: 'bg-blue-50',   border: 'border-blue-200',  badge: 'bg-blue-100 text-blue-800',    dot: 'bg-blue-500'  },
  'Contingency Reserve Fund':{ bg: 'bg-amber-50',  border: 'border-amber-200', badge: 'bg-amber-100 text-amber-800',  dot: 'bg-amber-500' },
};

export default function ChartOfAccounts() {
  const [codes, setCodes] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    glCodes.list().then(r => setCodes(r.data)).catch(() => {});
  }, []);

  const filtered = search.trim()
    ? codes.filter(g =>
        g.code.includes(search) ||
        g.name.toLowerCase().includes(search.toLowerCase()) ||
        g.type.toLowerCase().includes(search.toLowerCase())
      )
    : codes;

  const grouped = filtered.reduce((acc, g) => {
    (acc[g.type] = acc[g.type] || []).push(g);
    return acc;
  }, {});

  const ORDER = ['Income', 'Operating Expense', 'Contingency Reserve Fund'];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Chart of Accounts</h1>
        <p className="text-gray-500 text-sm mt-1">
          Standard BC strata GL codes — assign these to invoices for accountant-ready exports.
        </p>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3 mb-6">
        <svg className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="text-sm text-blue-800">
          <p className="font-semibold mb-0.5">How to use</p>
          <p>Open any invoice, expand it, and select a GL code from the dropdown. The export CSV will include a dedicated GL Code column compatible with QuickBooks, Sage, and Xero manual import.</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-5">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by code, name, or type…"
            className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Grouped code sections */}
      <div className="space-y-6">
        {ORDER.filter(t => grouped[t]?.length).map(type => {
          const c = TYPE_COLORS[type] || TYPE_COLORS['Operating Expense'];
          return (
            <div key={type} className={`rounded-xl border ${c.border} overflow-hidden`}>
              {/* Section header */}
              <div className={`${c.bg} px-5 py-3 flex items-center gap-3 border-b ${c.border}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${c.dot} shrink-0`} />
                <h2 className="font-semibold text-gray-900">{type}</h2>
                <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium ${c.badge}`}>
                  {grouped[type].length} accounts
                </span>
              </div>

              {/* Code rows */}
              <div className="bg-white divide-y divide-gray-100">
                {grouped[type].map(g => (
                  <div key={g.code} className="flex items-center px-5 py-3 hover:bg-gray-50 transition-colors">
                    <span className="font-mono text-sm font-bold text-gray-700 w-14 shrink-0">{g.code}</span>
                    <span className="text-sm text-gray-900 flex-1">{g.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.badge} shrink-0`}>
                      {g.code.startsWith('4') ? 'Income' : g.code.startsWith('7') ? 'CRF' : 'Expense'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          No GL codes match your search.
        </div>
      )}

      {/* Footer note */}
      <p className="text-xs text-gray-400 mt-6 text-center">
        Based on standard BC Strata Property Act accounting categories · Codes 4100–7900
      </p>
    </div>
  );
}
